/**
 * Dataset Filter Component
 * This component is used to show the filter section of the dataset
 * It is conditionally rendered based on the length of the tempTable
 * The filter section is shown only when the user clicks on the filter icon
 * The filter section is hidden when the user clicks on the filter icon again
 */

import React, { useState } from 'react';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { NotificationDialog } from '../CommonFunctions/DialogComponents';
import UserFilterDataset from './UserFilterDataset';
import { IFilter } from './types/BottomBarInterfaces';
import ShortUniqueId from 'short-unique-id';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { TAlert } from '../../types/types';

const DatasetFilter = ({
  editMode,
  dataSetFilterArray,
  setDataSetFilterArray,
  isDataSetVisible,
  setIsDataSetVisible,
}: {
  editMode?: boolean;
  dataSetFilterArray: IFilter[];
  setDataSetFilterArray: React.Dispatch<React.SetStateAction<IFilter[]>>;
  isDataSetVisible: boolean;
  setIsDataSetVisible: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { selectedTables, canEdit } = useSelector((state: RootState) => ({
    selectedTables: state.dataset.selectedTables,
    canEdit: state.dataset.canEdit,
  }));
  const [alert, setAlert] = useState<TAlert | null>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const handleDrop = (e: any) => {
    e.stopPropagation();
    if (!canEdit) {
      setAlert({
        message: 'You do not have permission to edit or modify the dataset',
        severity: 'warning',
      });
      return;
    }
    if(e.dataTransfer.getData('isFlatFile') === 'true'){
      setAlert({
        message: 'Dataset filter is not supported for flat file tables.',
        severity: 'warning',
      });
      return;
    }
    const tableHasCustomQuery = e.dataTransfer.getData('tableHasCustomquery');
    if (tableHasCustomQuery === 'true') {
      setAlert({
        message: 'Filter is disabled for tables with custom queries.',
        severity: 'warning',
      });
      return;
    }
    const isWebApiColumn = e.dataTransfer.getData('isWebApi');
    if (isWebApiColumn === 'true') {
      setAlert({
        message: 'Dataset filter is not supported for web API tables.',
        severity: 'warning',
      });
      return;
    }
    const refs = {
      isSelected: true,
      tableId: e.dataTransfer.getData('tableId'),
      index: e.dataTransfer.getData('connectIndex'),
      dataType: e.dataTransfer.getData('connectitemtype'),
      startTableName: e.dataTransfer.getData('connectTableName'),
      startColumnName: e.dataTransfer.getData('connectColumnName'),
      start: e.dataTransfer.getData('connectItemId'),
      table1_uid: e.dataTransfer.getData('connecttableUid'),
      schema: e.dataTransfer.getData('schema'),
      database: e.dataTransfer.getData('database'),
      startId: e.dataTransfer.getData('tableId'),
      connectionId: e.dataTransfer.getData('dbconnectionId'),
    };

    const uid: any = new ShortUniqueId({ length: 4 });
    const fieldFilterType = ['decimal', 'float', 'double', 'integer'].includes(refs.dataType)
      ? 'searchCondition'
      : 'pickList';
    const fieldOperator = fieldFilterType === 'pickList' ? 'in' : 'greaterThan';
    
    const field: IFilter = {
      tableId: refs.tableId,
      fieldName: refs.startColumnName,
      filterType: fieldFilterType,
      exprType: 'greaterThan',
      dataType: refs.dataType,
      shouldExclude: false,
      userSelection: [],
      fieldtypeoption: 'Pick List', //default value for
      includeexclude: 'Include', //default value for
      operator: fieldOperator,
      timeGrain: 'year',
      isTillDate: false,
      displayName: refs.startColumnName,
      uid: uid(),
      tableName: refs.startTableName,
      schema: refs.schema === 'undefined' ? undefined : refs.schema,
      database: refs.database === 'undefined' ? undefined : refs.database,
      connectionId: refs.connectionId,
    };
    setDataSetFilterArray((prev) => [...prev, field]);
  };
  if (selectedTables.length === 0) return null;

  return (
    <div
      className="filter_dataset"
      style={{
        width: isDataSetVisible ? '13.4rem' : '2.7rem',
        display: 'flex',
        flexDirection: 'column',
        position: 'absolute',
        paddingInline: isDataSetVisible ? '0.5rem' : 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: isDataSetVisible ? 'space-between' : 'center',
          alignItems: 'center',
          width: isDataSetVisible ? '11.8rem' : '2.7rem',
          paddingLeft: isDataSetVisible ? '0.200rem' : '',
          zIndex: '98',
        }}
      >
        <img
          src="/filter_icon_14_12_24.png"
          onClick={() => !isDataSetVisible && setIsDataSetVisible(!isDataSetVisible)}
          style={{
            height: '1.125rem',
            marginRight: !isDataSetVisible ? '6px' : '',
          }}
          alt="filter"
        />
        {isDataSetVisible && (
          <>
            <span className="axisTitle">Dataset Filter</span>
            <div>
              <button
                title="minimize"
                style={{
                  backgroundColor: 'white',
                  outline: 'none',
                  border: 'none',
                  padding: '0',
                }}
              >
                <ArrowBackRoundedIcon
                  style={{
                    right: '92%',
                    top: '0px',
                    zIndex: '999',
                    transform: 'rotate(180deg)',
                    color: 'gray',
                  }}
                  onClick={() => setIsDataSetVisible(!isDataSetVisible)}
                />
              </button>
            </div>
          </>
        )}
      </div>
      <div
        onDrop={(e) => {
          setIsDragOver(false);
          isDataSetVisible && handleDrop(e);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDragOver={(e) => {
          isDataSetVisible && e.preventDefault();
          setIsDragOver(true);
        }}
        style={{
          width: '100%',
          height: '100%',
          border: '1px solid transparent',
          position: 'relative',
        }}
        className={`dropzone ${isDragOver ? 'dropzone-highlighted' : ''}`}
      >
        {alert && (
          <NotificationDialog
            onCloseAlert={() => {
              setAlert(null);
            }}
            openAlert={!!alert}
            severity={alert.severity}
            testMessage={alert.message}
          />
        )}
        <div style={{ position: 'absolute', width: '100%' }}>
          {isDataSetVisible && (
            <>
              {dataSetFilterArray.length > 0 ? (
                <UserFilterDataset
                  editMode={editMode}
                  filters={dataSetFilterArray}
                  setDataSetFilterArray={setDataSetFilterArray}
                  canEdit={canEdit}
                />
              ) : (
                <div
                  style={{
                    padding: '1rem',
                    color: '#666',
                    fontSize: '0.9rem',
                    textAlign: 'center',
                    opacity: 0.8,
                    marginTop: '10px',
                  }}
                >
                  Please drag and drop any of the columns in the available table
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DatasetFilter;
